/**
 * recurrenceUtils.js
 *
 * Helpers do fluxo de criação de agendamentos recorrentes.
 *
 * A recorrência é apenas uma ferramenta de criação: estes utilitários calculam
 * datas, resumem a etapa de revisão e montam a mensagem única de confirmação.
 * Nenhum vínculo de série é criado — cada ocorrência gera um agendamento
 * individual e independente.
 *
 * Os limites aqui devem espelhar public/api/appointments/RecurrenceLimits.php.
 * O backend revalida tudo, mas mantemos os limites no frontend para dar
 * feedback imediato ao usuário.
 */

import { formatMessagePrice } from '../../../../utils/appointmentWhatsAppUtils';

/** Mínimo de ocorrências (1 ocorrência = agendamento único) */
export const MIN_OCCURRENCES = 2;

/** Máximo de ocorrências por recorrência */
export const MAX_OCCURRENCES = 12;

/**
 * Frequências oferecidas na interface, em dias.
 *
 * Presets em vez de um campo numérico livre: cobre os casos reais das clínicas
 * (sessões diárias, alternadas, semanais e mensais) sem exigir que o usuário
 * pense em "quantos dias".
 *
 * Deve espelhar INTERVAL_DAYS_OPTIONS de RecurrenceLimits.php.
 */
export const INTERVAL_OPTIONS = [
  { days: 1,  label: 'Todos os dias' },
  { days: 2,  label: 'Dia sim, dia não' },
  { days: 7,  label: 'Toda semana' },
  { days: 14, label: 'A cada 2 semanas' },
  { days: 21, label: 'A cada 3 semanas' },
  { days: 28, label: 'A cada 4 semanas' },
];

/** Quantidade padrão de ocorrências */
export const DEFAULT_OCCURRENCES = 4;

/** Intervalo padrão: semanal, o caso mais comum */
export const DEFAULT_INTERVAL_DAYS = 7;

/** Status possíveis de uma ocorrência calculada */
export const OCCURRENCE_STATUS = {
  AVAILABLE: 'available',
  UNAVAILABLE: 'unavailable',
  CLOSED: 'closed',
};

const WEEKDAY_NAMES = [
  'Domingo',
  'Segunda-feira',
  'Terça-feira',
  'Quarta-feira',
  'Quinta-feira',
  'Sexta-feira',
  'Sábado',
];

/**
 * Formata uma data para YYYY-MM-DD usando o fuso local
 * (evita o deslocamento de dia causado por toISOString()).
 *
 * @param {Date} date
 * @returns {string}
 */
export const formatDateKey = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/**
 * Formata HH:MM a partir de hora e minuto.
 *
 * @param {number} hour
 * @param {number} minute
 * @returns {string}
 */
export const formatTimeKey = (hour, minute) =>
  `${String(hour ?? 0).padStart(2, '0')}:${String(minute ?? 0).padStart(2, '0')}`;

/**
 * Converte YYYY-MM-DD em Date local (sem deslocamento de fuso).
 *
 * @param {string} dateKey
 * @returns {Date}
 */
export const parseDateKey = (dateKey) => {
  const [y, m, d] = String(dateKey).split('-').map(Number);
  return new Date(y, m - 1, d);
};

/**
 * Formata a data de uma ocorrência para exibição curta: "Segunda-feira, 10/08".
 *
 * @param {string} dateKey - YYYY-MM-DD
 * @returns {string}
 */
export const formatOccurrenceDate = (dateKey) => {
  const date = parseDateKey(dateKey);
  const weekday = WEEKDAY_NAMES[date.getDay()];
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${weekday}, ${day}/${month}`;
};

/**
 * Valida os parâmetros de configuração da recorrência.
 *
 * @param {number} intervalDays
 * @param {number} occurrences
 * @returns {string|null} Mensagem de erro ou null se válido
 */
export const validateRecurrenceConfig = (intervalDays, occurrences) => {
  const days = Number(intervalDays);
  const count = Number(occurrences);

  if (!INTERVAL_OPTIONS.some((opt) => opt.days === days)) {
    return 'Selecione uma frequência de repetição válida.';
  }

  if (!Number.isInteger(count) || count < MIN_OCCURRENCES || count > MAX_OCCURRENCES) {
    return `Quantidade deve ser entre ${MIN_OCCURRENCES} e ${MAX_OCCURRENCES} agendamentos.`;
  }

  return null;
};

/**
 * Estado inicial de decisão de cada ocorrência retornada pelo preview.
 *
 * IMPORTANTE: `skipped` representa exclusivamente uma DECISÃO DO USUÁRIO.
 * Um conflito de agenda não é "ignorado" — ele é "indisponível" e permanece
 * pendente de resolução até que o usuário escolha outro horário ou opte por
 * ignorar aquela data. Misturar os dois conceitos torna o feedback confuso.
 *
 * - Disponível           → `selectedTime` = horário original
 * - Indisponível/fechado → `selectedTime` = null (pendente de resolução)
 *
 * @param {Array} occurrences - Ocorrências vindas do preview
 * @returns {Array} Ocorrências com os campos de decisão (`skipped`, `selectedTime`)
 */
export const initOccurrenceDecisions = (occurrences) =>
  (occurrences || []).map((occ) => ({
    ...occ,
    skipped: false,
    selectedTime: occ.status === OCCURRENCE_STATUS.AVAILABLE ? occ.time : null,
  }));

/**
 * Estado visual de uma ocorrência na etapa de revisão.
 *
 * Deriva o rótulo a partir da combinação entre o status vindo do backend e a
 * decisão do usuário, garantindo uma única fonte de verdade para a UI.
 *
 * @param {Object} occurrence
 * @returns {'skipped'|'available'|'changed'|'closed'|'unavailable'}
 */
export const getOccurrenceState = (occurrence) => {
  if (!occurrence) return 'unavailable';

  // Decisão explícita do usuário tem prioridade
  if (occurrence.skipped) return 'skipped';

  if (occurrence.status === OCCURRENCE_STATUS.AVAILABLE) return 'available';

  // Conflito resolvido escolhendo outro horário do mesmo dia
  if (occurrence.selectedTime) return 'changed';

  if (occurrence.status === OCCURRENCE_STATUS.CLOSED) return 'closed';

  return 'unavailable';
};

/**
 * Resume o estado da etapa de revisão, para o usuário entender rapidamente
 * quantos agendamentos serão realmente criados.
 *
 * @param {Array} occurrences - Ocorrências com decisões aplicadas
 * @returns {{total: number, toCreate: number, available: number, changed: number, unresolved: number, skipped: number}}
 */
export const summarizeOccurrences = (occurrences) => {
  const list = occurrences || [];
  const states = list.map(getOccurrenceState);

  return {
    total: list.length,
    // Serão criados: horário original mantido + conflitos resolvidos
    toCreate: states.filter((s) => s === 'available' || s === 'changed').length,
    available: states.filter((s) => s === 'available').length,
    changed: states.filter((s) => s === 'changed').length,
    // Conflitos ainda pendentes de decisão — precisam de atenção do usuário
    unresolved: states.filter((s) => s === 'unavailable' || s === 'closed').length,
    skipped: states.filter((s) => s === 'skipped').length,
  };
};

/**
 * Extrai os itens aprovados para envio ao backend.
 *
 * @param {Array} occurrences - Ocorrências com decisões aplicadas
 * @returns {Array<{date: string, time: string}>}
 */
export const buildItemsToCreate = (occurrences) =>
  (occurrences || [])
    .filter((o) => !o.skipped && o.selectedTime)
    .map((o) => ({ date: o.date, time: o.selectedTime }));

/**
 * Monta a mensagem única de confirmação para múltiplas datas.
 *
 * Reaproveita o template de confirmação de agendamento individual (mesmos
 * placeholders e mesma escolha entre template completo/resumido) usando a
 * PRIMEIRA ocorrência, e acrescenta ao final a lista de todas as datas.
 *
 * Assim não é necessário criar um template novo nem alterar os templates
 * existentes da empresa.
 *
 * @param {Object} params
 * @param {string} params.template - Template base já escolhido
 * @param {Array} params.items - [{ date, time }] das ocorrências que serão criadas
 * @param {string} params.patientFirstName
 * @param {string} params.doctorName
 * @param {Object} params.companyConfig
 * @param {number|string} params.price
 * @param {Array<string>} params.months - Nomes dos meses (padrão do projeto)
 * @returns {string} Mensagem final (ainda em texto puro, sem conversão WhatsApp)
 */
export const buildRecurringConfirmationMessage = ({
  template,
  items,
  patientFirstName,
  doctorName,
  companyConfig,
  price,
  months,
}) => {
  if (!template || !items || items.length === 0) return '';

  const first = items[0];
  const firstDate = parseDateKey(first.date);

  const day = String(firstDate.getDate()).padStart(2, '0');
  const monthName = months?.[firstDate.getMonth()] || '';
  const dayOfWeek = WEEKDAY_NAMES[firstDate.getDay()];

  let message = template;

  message = message.replace('{name}', patientFirstName || '');
  message = message.replace('{dayOfWeek}', dayOfWeek);
  message = message.replace('{day}', day);
  message = message.replace('{month}', monthName);
  message = message.replace('{time}', first.time);
  message = message.replace('{when}', dayOfWeek);
  message = message.replace('{doctor_name}', doctorName || '');
  message = message.replace('{company_name}', companyConfig?.Name || '');
  message = message.replace(
    '{address}',
    `${companyConfig?.Address || ''}, ${companyConfig?.Number || ''}`
  );
  message = message.replace('{price}', formatMessagePrice(price));

  // Lista completa das ocorrências criadas — apenas quando há mais de uma
  if (items.length > 1) {
    const lines = items
      .map((item) => `• ${formatOccurrenceDate(item.date)} às ${item.time}`)
      .join('\n');

    message += `\n\nSeus próximos atendimentos:\n${lines}`;
  }

  return message;
};
